import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import OrbitingIcons from "../OrbitingIcons/OrbitingIcons";
import { useAppConfig } from "../../../appConfig";
import VerifyMLSAccountModal from "../../Onboarding/VerifyMLSAccountModal";
import LoginModal from "../../Onboarding/UserRegister/LoginModal";
import ResetPassword from "../../Onboarding/UserRegister/ResetPassword";
import { resetPassword } from "../../../services/v2/loginService";
import {
  fitsRelayState,
  locationToPath,
  rememberPostLoginRedirect,
  withRedirectParam,
} from "../../../helpers/postLoginRedirect";
import "./TenantLoginPage.scss";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@/helpers/legalLinks";

// Scopes the portaled login/reset modals above .tenant-login (z-index 9999).
// Mirrors the VerifyMLSAccountModal pattern (see TenantLoginPage.scss).
const TENANT_MODAL_CLASS = "tenant-login-modal";

const DEFAULT_SPLASH_GRADIENT =
  "linear-gradient(135deg, #060d1a 0%, #0a1628 50%, #0f1f3a 100%)";

const TenantLoginPage = ({ ssoError, mlsLinkState, onVerifySuccess, onVerifyClose }) => {
  const { tenant } = useAppConfig() || {};
  const location = useLocation();
  const returnTo = locationToPath(location.state?.from);
  const themeVars = tenant?.themeVars || {};
  const splashGradient =
    themeVars["--splash-gradient"] || DEFAULT_SPLASH_GRADIENT;
  const accentColor = themeVars["--tenant-accent-color"] || "#93c5fd";
  const logoSrc = tenant?.assets?.logos?.logoWhite || "";

  const [showLogin, setShowLogin] = useState(false);
  const [resetPwdModal, setResetPwdModal] = useState({ showResetModal: false });
  const [resetPwdModalType, setResetPwdModalType] = useState(null);

  useEffect(() => {
    if (tenant?.head?.title) document.title = tenant.head.title;
  }, [tenant?.head?.title]);

  const handleResetPwdModal = ({ showResetModal, resetPwdModalType: type }) => {
    setShowLogin(false);
    setResetPwdModalType(type);
    setResetPwdModal({ showResetModal });
  };

  const resetPassWord = async (params) => {
    if (!params?.email) return null;
    const response = await resetPassword(params);
    if (response?.errorCode === 0) {
      setResetPwdModal({ showResetModal: false });
      toast.success("Password reset link sent to your email.");
      return;
    }
    return { isVerified: false, message: response?.message || "Something went wrong." };
  };

  const continueWithMls = () => {
    if (tenant?.ssoProvider) {
      const baseAPIURL = process.env.REACT_APP_API_BASE_URL;
      rememberPostLoginRedirect(returnTo);
      if (tenant.ssoType === "saml") {
        return (window.location.href = withRedirectParam(
          `${baseAPIURL}api/v2/auth/saml/${tenant.ssoProvider}/login`,
          // Too long for RelayState: SsoCallback falls back to the remembered path.
          returnTo && fitsRelayState(returnTo) ? returnTo : null
        ));
      }
      if (tenant.ssoType === "oicd") {
        return (window.location.href = withRedirectParam(
          `${baseAPIURL}api/v2/auth/oauth/${tenant.ssoProvider}/authorize`,
          returnTo
        ));
      }
    }
  };

  const handleLoginSuccess = () => {
    if (returnTo) {
      window.location.replace(returnTo);
      return;
    }
    window.location.reload();
  };

  return (
    <section
      className="tenant-login"
      style={{ "--tenant-accent-color": accentColor }}
    >
      <div
        className="tenant-login__visual"
        style={{ background: splashGradient }}
      >
        {logoSrc && (
          <img className="tenant-login__logo" src={logoSrc} alt="Logo" />
        )}
        <OrbitingIcons />
        <span
          className="tenant-login__anchor"
          style={{
            background: accentColor,
            boxShadow: `0 0 24px 8px ${accentColor}33`,
          }}
        />
      </div>

      <div className="tenant-login__panel">
        {logoSrc && (
          <img
            className="tenant-login__logo tenant-login__logo--mobile"
            src={logoSrc}
            alt="Logo"
          />
        )}

        <div className="tenant-login__card">
          <h1 className="tenant-login__title">Welcome back</h1>
          <p className="tenant-login__subtitle">
            Sign in to access real-time buyer demand data for your market.
          </p>

          {ssoError && (
            <div className="tenant-login__error">{ssoError}</div>
          )}

          <div className="tenant-login__choices">
            <button
              type="button"
              className="btn btn-primary tenant-login__sso"
              onClick={continueWithMls}
            >
              <span className="tenant-login__sso-label">Continue with MLS</span>
            </button>

            <Button
              variant="secondary"
              className="btn-tweak w-100 tenant-login__signin"
              onClick={() => setShowLogin(true)}
            >
              Sign In
            </Button>
          </div>

          <p className="tenant-login__legal">
            By continuing, you agree to our{" "}
            <a
              href={TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>
            . Read our{" "}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
      <LoginModal
        className={TENANT_MODAL_CLASS}
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLoginSuccess={handleLoginSuccess}
        handleResetPwdModal={handleResetPwdModal}
      />

      <ResetPassword
        className={TENANT_MODAL_CLASS}
        resetPwdModal={resetPwdModal}
        resetPwdModalType={resetPwdModalType}
        resetPassWord={resetPassWord}
        handleResetPwdModal={handleResetPwdModal}
        handleAgentOnboardingModal={() => setShowLogin(true)}
      />

      {mlsLinkState?.show && (
        <VerifyMLSAccountModal
          show={mlsLinkState.show}
          pendingToken={mlsLinkState.pendingToken}
          maskedEmail={mlsLinkState.maskedEmail}
          onSuccess={onVerifySuccess}
          onClose={onVerifyClose}
        />
      )}
    </section>
  );
};

export default TenantLoginPage;
